import { formatAppDateTime } from '../../../services/timezone';
/*
 * Shared helpers used by every per-module detail config. Kept JSX-free so the
 * config files stay pure data + small functions.
 */

export const CURRENCY_SYMBOL = { USD: '$', INR: '₹', EUR: '€', GBP: '£', AUD: '$', CAD: '$' };

export const isEmpty = (value) =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

export const has = (value) => !isEmpty(value);

export function capitalize(text) {
  const str = asText(text);
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function lookupId(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)
    && 'id' in value && 'value' in value) {
    return value.id ?? value.value;
  }
  return value;
}

export function asText(value) {
  if (Array.isArray(value)) {
    return value
      .filter((item) => !isEmpty(item))
      .map((item) => asText(item))
      .filter(Boolean)
      .join(', ');
  }
  if (value && typeof value === 'object' && 'id' in value && 'value' in value) {
    return String(value.value ?? value.id ?? '');
  }
  return String(value ?? '');
}

export function symbol(currency) {
  if (isEmpty(currency)) return '';
  return CURRENCY_SYMBOL[String(currency).toUpperCase()] ?? String(currency);
}

// "Jun 17, 2026 | 07:31PM" — used by the header "Created on" line.
/**
 * formatDateTime — an instant, as the tenant has configured it.
 *
 * This used to build the string by hand with toLocaleDateString /
 * toLocaleTimeString, which meant two things it should not have meant:
 *
 *   • the format ("Aug 06, 2026 | 07:03PM") was hardcoded here, so changing it
 *     anywhere meant changing it in code;
 *   • it rendered in the BROWSER's zone. Two colleagues in different countries
 *     opening the same record saw different times, with nothing on screen to
 *     say which was which.
 *
 * Both are now config: Admin → Display Defaults sets the format and the zone,
 * and the zone's short name is appended so a timestamp says which clock it is
 * on. Because this is render-time, EXISTING records pick it up immediately —
 * nothing in the database changes.
 */
export function formatDateTime(value) {
  if (isEmpty(value)) return '';
  return formatAppDateTime(value);
}

// Formats a rate/budget into a compact string e.g. "₹34 - 55/Monthly" or "$80/Hour".
// Returns '' when there's no usable start value so callers can `has()`-guard it.
export function formatRate({ start, end, sym = '', unit } = {}) {
  if (isEmpty(start) || Number(start) <= 0) return '';
  const range = !isEmpty(end) && Number(end) > 0 ? `${start} - ${end}` : `${start}`;
  return `${sym}${range}${isEmpty(unit) ? '' : `/${unit}`}`;
}

// Light colour mapping for the status pill.
export function statusColor(value) {
  const key = asText(value).toLowerCase();
  if (['active', 'open', 'hired', 'approved', 'fulfilled', 'submitted'].includes(key)) return 'success';
  if (['inactive', 'closed', 'rejected', 'high'].includes(key)) return 'error';
  if (['hold', 'on hold', 'pending', 'unread', 'medium'].includes(key)) return 'warning';
  return 'default';
}
